import { Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { QuestionnaireInstanceService } from '../../../api/services/questionnaire-instance.service';
import { QuestionsService } from '../../../services/questions.service';
import { QuestionnaireInstanceModel } from '../../../api/models/questionnaire-instance-model';
import { QuestionnaireInstanceStatus } from '../../../api/models/questionnaire-instance-status';
import { StrictHttpResponse } from '../../../api/strict-http-response';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-invite-link-loading',
  templateUrl: './invite-link-loading.component.html',
  standalone: false,
})
export class InviteLinkLoadingComponent {
  inviteToken: string = '';

  private destroyRef = inject(DestroyRef);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private questionnaireInstanceService: QuestionnaireInstanceService,
    private questionsService: QuestionsService,
  ) {
    route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((parameters: Params) => {
        this.inviteToken = parameters['token'];
      });

    if (!this.inviteToken) {
      void this.router.navigate(['/error/not-found']);
    }
  }

  ngOnInit() {
    // Use token to get questionnaire instance
    this.questionnaireInstanceService
      .inviteGet$Json$Response({
        token: this.inviteToken,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const instance = response.body;
          if (instance.status == QuestionnaireInstanceStatus.Initialised) {
            this.handleNewQuestionnaireInstance(instance);
          }
          if (
            instance.status == QuestionnaireInstanceStatus.Started ||
            instance.status == QuestionnaireInstanceStatus.Resumed
          ) {
            this.handleResumeQuestionnaireInstance(instance);
          }
          if (
            instance.status == QuestionnaireInstanceStatus.FinishedEarly ||
            instance.status == QuestionnaireInstanceStatus.Finished
          ) {
            this.handleFinishedQuestionnaireInstance();
          }
        },
        error: (response: StrictHttpResponse<void>) => {
          // If we get a 410 response, the link is expired, so we should show the user an error view
          if (response.status === 410) {
            void this.router.navigate(['/questionnaire-unavailable']);
          } else {
            console.error(response);
          }
        },
      });
  }

  handleNewQuestionnaireInstance(instance: QuestionnaireInstanceModel) {
    this.questionnaireInstanceService
      .startPost$Json({
        questionnaireInstanceGuid: instance.guid!,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((question) => {
        this.questionnaireInstanceService.questionnaireInstanceGuid =
          instance.guid!;
        this.questionsService.setCurrentQuestion(question);

        void this.router.navigate(['questions'], {
          state: {
            activeQuestionnaireInstanceGuid: instance.guid,
          },
        });
      });
  }

  handleResumeQuestionnaireInstance(instance: QuestionnaireInstanceModel) {
    if (this.questionsService.resumeInstance(instance.guid!)) {
      void this.router.navigate(['questions'], {
        state: {
          activeQuestionnaireInstanceGuid: instance.guid,
        },
      });
    }
  }

  handleFinishedQuestionnaireInstance() {
    void this.router.navigate(['completed']);
  }
}
